import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchAssessment, resetAssessment } from '../../app/store/assessmentSlice';
import { setAllQuestionStatus, setBestPractice, setBPStatus, setCertificationControlIds, setDomain, setInclusionConfigsById, setQuestion, setQuestionnaire, setQuestions, setTopic } from '../../app/store/questionnaireAnswerSlice';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import { Domain } from '../../interfaces/domain.interface';
import { QuestionAnswer } from '../../interfaces/questionAnswer.interface';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import { Topic } from '../../interfaces/topic.interface';
import { buildOutQuestionnaire, initialBuildOutQuestionnaire } from '../../services/buildQuestionnaire';
import { QuestionnaireTypes, buildFullQuestionnaireObject, fetchDetailedQuestionnaire } from '../../services/questionnaires';
import useGradualProgress from './useGradualProgress';
import { Box, Backdrop, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { batch } from 'react-redux';
import { BLUE_BADGE_IMAGE } from '../../services/badge';
import { AssessmentStatus, AssessmentStatusesSet } from '../../services/assessments';
import { buildQuestionsFromDetailedQuestionnaire } from '../../services/reports';
import { InclusionConfiguration } from '../../interfaces/inclusionConfiguration.interface';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const useStyles = makeStyles(() => ({
  backdrop: {
    backgroundColor: 'rgba(0,0,0,.5)',
    zIndex: '999999999',
  },
  loaderContainer: {
    width: '500px',
    maxWidth: '90%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    color: 'white',
  },
  loaderBadgeContainer: {
    width: '100%',
    height: '402px',
    maxHeight: '90%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loaderBadge: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  clipPath: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'clip-path 1s linear',
    width: '100%',
    height: '100%',
  },
  percentText: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  percentTextTitle: {
    fontSize: '20px',
    fontWeight: 'semibold',
  },
}));

export default function QuestionnaireAnswerLoader(props: {
  questionnaire: Questionnaire,
  setHideLoader: (hide: boolean) => void,
  domain?: Domain,
  topic?: Topic,
  bestPractice?: BestPractice,
}) {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const params = useParams();
  const query = useQuery();
  const [isQuestionnaireBuilderComplete, setIsQuestionnaireBuilderComplete] = useState(false);
  const assessmentId:string | undefined = params.assessmentId;
  const questionId:string | null = query.get('question');
  const { assessment } = useAppSelector(state => state.assessments);
  const { survey } = useAppSelector(state => state.surveys);
  const { workflow } = useAppSelector(state => state.workflow);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [buildingQuestionnaire, setBuildingQuestionnaire] = useState(false);
  const {
    questionnaire,
    questions,
    preview,
    bpStatus,
    questionStatus,
    assessmentQuestionsStatus,
  } = useAppSelector(state => state.questionnaireAnswer);
   
  const progressPercent = useGradualProgress(1, 75, 88, 98, 200, loadingFinished, props.setHideLoader, 50, 100, 2);

  const requiresAssessmentQuestionsStatus: boolean =
    Boolean(
      workflow &&
      (survey?.questionnaire as Questionnaire)?.type === QuestionnaireTypes.MPA_BEST_PRACTICE &&
      AssessmentStatusesSet.has(workflow.status as AssessmentStatus),
    );

  const hasAssessmentQuestionsStatus = useMemo(
    () => Boolean(assessmentQuestionsStatus && Object.keys(assessmentQuestionsStatus).length),
    [assessmentQuestionsStatus],
  );

  const isAssessmentQuestionStatusFulfilled: boolean = useMemo(
    () => ((!requiresAssessmentQuestionsStatus && !!workflow) || hasAssessmentQuestionsStatus),
    [requiresAssessmentQuestionsStatus, workflow, hasAssessmentQuestionsStatus],
  );

  useEffect(() => {
    if (
      questionnaire &&
      isQuestionnaireBuilderComplete &&
      ((isAssessmentQuestionStatusFulfilled && !!survey?.bestPracticeStatuses?.length) || preview)
    ) {
      setLoadingFinished(true);
    }
  }, [
    isAssessmentQuestionStatusFulfilled,
    survey?.bestPracticeStatuses?.length,
    preview,
    isQuestionnaireBuilderComplete,
    questionnaire,
  ]);

  const buildAllInclusionsFromDetailedQuestionnaire = (detailedQuestionnaire: Questionnaire): InclusionConfiguration[] => {
    const allInclusions: InclusionConfiguration[] = [];

    detailedQuestionnaire.domains.forEach(domain => {
      domain.topics.forEach(topic => {
        topic.bestPractices.forEach(bp => {
          if (bp.inclusions && bp.inclusions.length > 0) {
            allInclusions.push(...(bp.inclusions as InclusionConfiguration[]));
          }
          bp.questions.forEach(question => {
            if (question.inclusions && question.inclusions.length > 0) {
              allInclusions.push(...(question.inclusions as InclusionConfiguration[]));
            }
          });
        });
      });
    });
    return allInclusions;
  };

  const buildQuestionnaire = useCallback(async () => {
    if (!preview && (!survey || questionnaire)) return;
    if (preview && questions !== undefined && questions.length > 0) return;
    if (buildingQuestionnaire) return;

    try {
      setBuildingQuestionnaire(true);
      const questionnaireObjects = await buildFullQuestionnaireObject(props.questionnaire.id);
      const detailedQuestionnaire = await fetchDetailedQuestionnaire(props.questionnaire.id);
      const builtQuestionnaire = initialBuildOutQuestionnaire( { ...questionnaireObjects, questionnaire: props.questionnaire } );
      const builtQuestions = buildQuestionsFromDetailedQuestionnaire(detailedQuestionnaire, props.questionnaire);
      const allInclusions = buildAllInclusionsFromDetailedQuestionnaire(detailedQuestionnaire);

      const { questionnaire: fullQuestionnaire, certControls, questionStatusById } = buildOutQuestionnaire(
        builtQuestionnaire.questionnaire,
        [ ...builtQuestions ],
        [...survey?.questionAnswers as QuestionAnswer[] || []],
        workflow?.workflowType,
      );

      batch(() => {
        dispatch(setInclusionConfigsById(allInclusions));
        dispatch(setCertificationControlIds(certControls));
        dispatch(setQuestions([ ...builtQuestions ]));
        dispatch(setAllQuestionStatus(questionStatusById));
        dispatch(setQuestionnaire(fullQuestionnaire));
        dispatch(setBPStatus(workflow?.workflowType));
      });
    } catch (error) {
      console.error('Error building questionnaire:', error);
    } finally {
      setBuildingQuestionnaire(false);
    }
  }, [preview, survey, questionnaire, buildingQuestionnaire, questions, props.questionnaire, dispatch, workflow]);

  const setValuesFromQuestion = () => {
    if (!questionnaire || !questions || !questionId) return false;

    const selectedQuestion = questions.find(cq => cq.id == questionId);
    if (!selectedQuestion) return false;

    const domainId = typeof selectedQuestion.domain === 'number' ? selectedQuestion.domain : selectedQuestion.domain.id;
    const nDomain = questionnaire.domains.find(d => d.id === domainId);
    if (!nDomain) return false;

    const topicId = typeof selectedQuestion.topic === 'number' ? selectedQuestion.topic : selectedQuestion.topic.id;
    const nTopic = nDomain.topics.find(t => t.id === topicId);
    if (!nTopic) return false;

    const bestPracticeId = typeof selectedQuestion.bestPractice === 'number' ? selectedQuestion.bestPractice : selectedQuestion.bestPractice.id;
    const nBP = nTopic.bestPractices.find(bp => bp.id === bestPracticeId);

    if (!nBP) return false;

    batch(() => {
      dispatch(setDomain(nDomain));
      dispatch(setTopic(nTopic));
      dispatch(setBestPractice(nBP));
      dispatch(setQuestion(selectedQuestion));
    });
    return true;
  };

  const handleSetFirstQuestion = useCallback(() => {
    if (!questionnaire) return;
    if (setValuesFromQuestion()) return;

    const domainId = props.domain ? props.domain.id : questionnaire.domains[0].id;
    const nDomain = questionnaire.domains.find(d => d.id === domainId);
    if (!nDomain) return;
    dispatch(setDomain(nDomain));

    const firstValidTopic = questionStatus ? nDomain.topics.find(t =>
      t.bestPractices.some(bp =>
        bp.questions.some(q => questionStatus[q.id]?.status !== 'hide'),
      ),
    ) : nDomain.topics[0];

    const topicId = props.topic ? props.topic.id : firstValidTopic?.id;
    const nTopic = nDomain.topics.find(t => t.id === topicId);
    if (!nTopic) return;
    dispatch(setTopic(nTopic));

    // Find the first best practice where any question is not hidden
    const firstValidBP = questionStatus && nTopic.bestPractices.find(bp =>
      bp.questions.some(q => questionStatus[q.id]?.status !== 'hide'),
    );

    // Determine the best practice ID
    const bestPracticeId = props.bestPractice
      ? props.bestPractice.id
      : firstValidBP
        ? firstValidBP.id
        : nTopic.bestPractices[0].id;

    const nBP = nTopic.bestPractices.find(bp => bp.id === bestPracticeId);

    if (!nBP) return;
    dispatch(setBestPractice(nBP));

    const nquestion = nBP.questions[0];
    if (!nquestion) return;
    dispatch(setQuestion(nquestion));
  }, [questionnaire, questionStatus, props.domain, props.topic, props.bestPractice, dispatch]);

  useEffect(() => {
    if (!survey || questionnaire || !workflow) return;
  
    (async () => {
      await buildQuestionnaire();
      setIsQuestionnaireBuilderComplete(true);
    })();
  }, [buildQuestionnaire, survey, workflow]);

  useEffect(() => {
    if (!bpStatus || !Object.keys(bpStatus).length || !bpStatus[Object.keys(bpStatus)[0]].id || !isAssessmentQuestionStatusFulfilled || !questionnaire) return;
    setLoadingFinished(true);
  }, [bpStatus, questionnaire, isAssessmentQuestionStatusFulfilled]);
  
  useEffect(() => {
    if (!params || assessment) return;

    dispatch(resetAssessment());
    if (assessmentId) dispatch(fetchAssessment(parseInt(assessmentId)));
  }, [params]);

  // Set first question on unmount
  const handleSetFirstQuestionRef = useRef(handleSetFirstQuestion);
  useEffect(() => {
    handleSetFirstQuestionRef.current = handleSetFirstQuestion;
  }, [handleSetFirstQuestion]);

  useEffect(() => {
    return () => {
      handleSetFirstQuestionRef.current();
    };
  }, []);

  return (
    <Backdrop className={classes.backdrop} open>
      <Box className={classes.loaderContainer}>
        <Box className={classes.loaderBadgeContainer}>
          <Box className={classes.clipPath} sx={{ clipPath: `polygon(0% 0%, 100% 0%, 100% ${100 - progressPercent}%, 0% ${100 - progressPercent}%);` }}><Box component='img' src={BLUE_BADGE_IMAGE} className={classes.loaderBadge} /></Box>
          <Box className={classes.clipPath} sx={{ clipPath: `polygon(0% ${100 - progressPercent}%, 0% 100%, 100% 100%, 100% ${100 - progressPercent}%)` }}><Box component='img' src={`${process.env.PUBLIC_URL}/assets/TPN_GoldLogoTransparent_1280.png`} className={classes.loaderBadge} /></Box>
        </Box>
        <Typography className={classes.percentText}>{progressPercent}%</Typography>
        <Typography className={classes.percentTextTitle}>Loading {props.questionnaire?.title}</Typography>
      </Box>
    </Backdrop>
  );
}
